/**
 * Shell UI state (one per tab): sidebar (docked collapse, width, drawer), palette, shortcut help, the pending key
 * sequence chip, the details pane, the session-expired dialog. MobX, so the dispatcher and components read the same
 * values synchronously.
 */
import { action, makeObservable, observable, observableRef } from 'mobx';
import { clampSidebarWidth, readCollapsedSections, readSidebarPref, writeCollapsedSections, writeSidebarPref } from '../prefs';
import type { PalettePage } from '../registry/types';

interface PaletteState {
  readonly page: PalettePage | null;
  readonly query: string;
  /** Scope chip ("ENG-12", "3 issues"): commands act on this target until it is cleared. */
  readonly chip: { readonly label: string; readonly ids: readonly string[]; readonly model: string } | null;
}

type SessionProblem = { readonly kind: 'expired' | 'restored' } | { readonly kind: 'account-changed'; readonly pending: number; readonly accountId: string };

class ShellUi {
  sidebarCollapsed = false;
  sidebarWidth = 240;
  /** The overlay drawer (< 1024 px). */
  drawerOpen = false;
  /** A collapsed docked sidebar revealed by hovering the left edge. */
  peekOpen = false;
  palette: PaletteState | null = null;
  helpOpen = false;
  /** The first key(s) of a pending sequence ("G"), shown by shortcuts.sequence-hint. */
  sequence: string | null = null;
  detailsOpen = true;
  session: SessionProblem | null = null;
  collapsedSections: Record<string, boolean> = {};
  /** Polite announcement text for app.announcer. */
  announcement = '';

  constructor() {
    makeObservable(this, {
      sidebarCollapsed: observable,
      sidebarWidth: observable,
      drawerOpen: observable,
      peekOpen: observable,
      palette: observableRef,
      helpOpen: observable,
      sequence: observable,
      detailsOpen: observable,
      session: observableRef,
      collapsedSections: observableRef,
      announcement: observable,
      toggleSidebar: action,
      setSidebarWidth: action,
      setDrawer: action,
      setPeek: action,
      openPalette: action,
      setPaletteQuery: action,
      setPalettePage: action,
      clearPaletteChip: action,
      closePalette: action,
      setHelp: action,
      setSequence: action,
      toggleDetails: action,
      setDetails: action,
      setSession: action,
      toggleSection: action,
      setSectionCollapsed: action,
      announce: action,
    });
    const p = readSidebarPref();
    this.sidebarCollapsed = p.collapsed;
    this.sidebarWidth = p.width;
    this.collapsedSections = readCollapsedSections();
  }

  /** `[` / app.sidebar-toggle: collapse the docked sidebar at ≥ 1024 px, open/close the drawer below. */
  toggleSidebar(compact: boolean): void {
    if (compact) {
      this.drawerOpen = !this.drawerOpen;
      return;
    }
    this.sidebarCollapsed = !this.sidebarCollapsed;
    this.peekOpen = false;
    writeSidebarPref({ width: this.sidebarWidth, collapsed: this.sidebarCollapsed });
  }

  setSidebarWidth(w: number, persist: boolean): void {
    this.sidebarWidth = clampSidebarWidth(w);
    if (persist) writeSidebarPref({ width: this.sidebarWidth, collapsed: this.sidebarCollapsed });
  }

  setDrawer(open: boolean): void {
    this.drawerOpen = open;
  }

  setPeek(open: boolean): void {
    this.peekOpen = open && this.sidebarCollapsed;
  }

  openPalette(o: { page?: PalettePage | null; query?: string; chip?: PaletteState['chip'] } = {}): void {
    this.helpOpen = false;
    this.palette = { page: o.page ?? null, query: o.query ?? '', chip: o.chip ?? this.palette?.chip ?? null };
  }

  setPaletteQuery(query: string): void {
    if (this.palette) this.palette = { ...this.palette, query };
  }

  setPalettePage(page: PalettePage | null): void {
    if (this.palette) this.palette = { ...this.palette, page, query: '' };
  }

  clearPaletteChip(): void {
    if (this.palette) this.palette = { ...this.palette, chip: null };
  }

  closePalette(): void {
    this.palette = null;
  }

  setHelp(open: boolean): void {
    this.helpOpen = open;
    if (open) this.palette = null;
  }

  setSequence(s: string | null): void {
    this.sequence = s;
  }

  toggleDetails(): void {
    this.detailsOpen = !this.detailsOpen;
  }

  setDetails(open: boolean): void {
    this.detailsOpen = open;
  }

  setSession(s: SessionProblem | null): void {
    this.session = s;
  }

  /**
   * An explicit open/closed choice, kept even when it matches the section's default — for sections whose default
   * depends on where you are (a settings team is open while you're on one of its pages), where toggleSection's
   * "absent = default" can't record "opened by hand".
   */
  setSectionCollapsed(id: string, collapsed: boolean): void {
    const next = { ...this.collapsedSections, [id]: collapsed };
    this.collapsedSections = next;
    writeCollapsedSections(next);
  }

  toggleSection(id: string, collapsed?: boolean): void {
    const next = { ...this.collapsedSections };
    const v = collapsed ?? !next[id];
    if (v) next[id] = true;
    else delete next[id];
    this.collapsedSections = next;
    writeCollapsedSections(next);
  }

  announce(text: string): void {
    // re-announce identical text by clearing first (live regions ignore unchanged content)
    this.announcement = '';
    this.announcement = text;
  }
}

export const shellUi = new ShellUi();

/** Close the phone layout's sidebar drawer (a feature that opens its own full-screen surface from the drawer). */
export function closeSidebarDrawer(): void {
  shellUi.setDrawer(false);
}
